import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { TextBoxSC } from 'atoms/TextBox'
import { COLOR, CORNERS, FIXED_INDENTS, MEDIA, TRANSITIONS } from 'vars/index'

/**
 * Season chips + title of the series details concept (Figma 16273:212314 at
 * 1920, 16273:216762 at 375): the "Season" label and numbered chips above
 * the season name, its labels and its description.
 *
 * Vertical gaps, chip padding and the header max-width step at the frames
 * the concept draws (375 / 600 / 1024 / 1440 / 1920). Horizontal padding
 * follows the same ladder the details area of this page already uses, so
 * no intermediate width is invented for the 1240 stop the series frames
 * skip.
 */
export const SeasonBlockSC = styled.div`
  --seasonBlockPaddingTop: var(${FIXED_INDENTS.INDENT_50});
  --seasonBlockPaddingHorizontal: var(${FIXED_INDENTS.INDENT_40});
  --seasonBlockRowGap: var(${FIXED_INDENTS.INDENT_40});
  --seasonChipsLabelGap: var(${FIXED_INDENTS.INDENT_40});
  --seasonChipPaddingX: var(${FIXED_INDENTS.INDENT_40});

  box-sizing: border-box;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  background: var(${COLOR.SURFACE_DARK});
  padding: var(--seasonBlockPaddingTop) var(--seasonBlockPaddingHorizontal) 0;
  row-gap: var(--seasonBlockRowGap);

  /* Figma 375 (16329:221967) / 600 (16329:221772): the How to Watch / Sign in
   * to watch action sits below the description and spans the full width. */
  > ${ButtonSC}[data-arcana-button='true'] {
    width: 100%;
  }

  ${MEDIA.MIN_600} {
    --seasonBlockPaddingTop: var(${FIXED_INDENTS.INDENT_60});
    --seasonBlockPaddingHorizontal: var(${FIXED_INDENTS.INDENT_80});
    --seasonBlockRowGap: var(${FIXED_INDENTS.INDENT_50});
    --seasonChipsLabelGap: var(${FIXED_INDENTS.INDENT_50});
    --seasonChipPaddingX: var(${FIXED_INDENTS.INDENT_50});
  }

  /*
   * Figma 1024 (16329:221576) / 1440 (16273:223513): the action moves beside
   * the season title instead of under the description. SeasonChipsRowSC is
   * width: 100%, so it always fills a wrapped line alone; the header and
   * the action then share the next line, and justify-content: space-between
   * pushes the action to the right edge, top-aligned with the title. This
   * only works because SeasonBlockHeaderSC caps its own max-width at every
   * step from here up — remove those caps and the action wraps to its own
   * third line again.
   */
  ${MEDIA.MIN_1024} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;

    > ${ButtonSC}[data-arcana-button='true'] {
      width: auto;
      flex-shrink: 0;
    }
  }

  ${MEDIA.MIN_1240} {
    --seasonBlockPaddingHorizontal: var(${FIXED_INDENTS.INDENT_160});
  }

  ${MEDIA.MIN_1440} {
    --seasonBlockPaddingTop: var(${FIXED_INDENTS.INDENT_80});
    --seasonBlockRowGap: var(${FIXED_INDENTS.INDENT_60});
    --seasonChipsLabelGap: var(${FIXED_INDENTS.INDENT_60});
    --seasonChipPaddingX: var(${FIXED_INDENTS.INDENT_60});
  }

  ${MEDIA.MIN_1640} {
    --seasonBlockPaddingHorizontal: var(${FIXED_INDENTS.INDENT_200});
  }
`

/**
 * `header` of the season block: season name, labels and description. The
 * concept keeps an 8px stack; the text column is capped at the widths the
 * 1024 / 1440 / 1920 frames draw (536 / 750 / 856).
 */
export const SeasonBlockHeaderSC = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-direction: column;
  align-items: flex-start;
  row-gap: var(${FIXED_INDENTS.INDENT_20});

  ${MEDIA.MIN_1024} {
    max-width: 536px;
  }

  ${MEDIA.MIN_1440} {
    max-width: 750px;
  }

  ${MEDIA.MIN_1920} {
    max-width: 856px;
  }
`

export const SeasonChipsRowSC = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  column-gap: var(--seasonChipsLabelGap, var(${FIXED_INDENTS.INDENT_40}));
  overflow: hidden;
`

export const SeasonChipsLabelSC = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: var(${FIXED_INDENTS.INDENT_20}) 0;
  /* new/title/small tracking: 3% of the 16px font size */
  letter-spacing: 0.48px;
`

export const SeasonChipsTrackSC = styled.div`
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  column-gap: var(${FIXED_INDENTS.INDENT_20});
  overflow-x: auto;
  /* the concept clips the row rather than showing a scrollbar under the chips */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    flex: 0 0 auto;
  }
`

/**
 * A `div`, not a `button`: the details skin binds `onClick` (same as tabs).
 */
export const SeasonChipSC = styled.div<{ selected: boolean }>`
  display: flex;
  box-sizing: border-box;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-height: var(${FIXED_INDENTS.INDENT_100});
  padding: var(${FIXED_INDENTS.INDENT_20})
    var(--seasonChipPaddingX, var(${FIXED_INDENTS.INDENT_40}));
  border-radius: var(${CORNERS.CORNERS_10});
  background-color: ${({ selected }) =>
    selected ? `var(${COLOR.SURFACE_OPACITY_LIGHT_10})` : 'transparent'};
  cursor: pointer;
  transition: background-color var(${TRANSITIONS.LINEAR_150});

  ${TextBoxSC} {
    white-space: nowrap;
  }

  ${MEDIA.NO_TOUCH} {
    &:hover {
      background-color: var(${COLOR.SURFACE_OPACITY_LIGHT_10});
    }
  }
`
